import { useEffect, useRef } from 'react';
import { graphql } from 'react-relay';

import type {
	UserPreferences,
	UserPreferencesProvider,
} from '@atlaskit/editor-common/types/user-preferences';
import type { UserPreferencesProvider as UserPreferencesProviderNext } from '@atlaskit/editor-common/user-preferences-provider';

import { useReadFragmentInterop } from '@confluence/apollo-relay-interop/entry-points/useReadFragmentInterop.ts';
import { getApolloClient } from '@confluence/graphql/entry-points/getApolloClient.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { queryInterop } from '@confluence/apollo-relay-interop/entry-points/query.ts';
import { mutateInterop } from '@confluence/apollo-relay-interop/entry-points/mutate.ts';
import { writeQueryInterop } from '@confluence/apollo-relay-interop/entry-points/writeQuery.ts';

// eslint-disable-next-line import/no-cycle
import {
	getUserPreferencesProvider,
	UserPreferencesPersistenceAPI,
} from './EditorUserPreferences.ts';
import type {
	EditorPreferencesQuery$data,
	EditorPreferencesQuery$variables,
} from './__generated__/EditorPreferencesQuery.graphql.ts';
import type {
	EditorPreferencesMutation$data,
	EditorPreferencesMutation$variables,
} from './__generated__/EditorPreferencesMutation.graphql.ts';

const EditorPreferencesQuery = graphql`
	query EditorPreferencesQuery {
		userPreferences {
			confluenceEditorSettings {
				toolbarDockingInitialPosition
			}
		}
	}
`;

const EditorPreferencesMutation = graphql`
	mutation EditorPreferencesMutation($toolbarDockingInitialPosition: String!) {
		updateUserPreferences(
			userPreferences: {
				confluenceEditorSettingsInput: {
					toolbarDockingInitialPosition: $toolbarDockingInitialPosition
				}
			}
		) {
			confluenceEditorSettings {
				toolbarDockingInitialPosition
			}
		}
	}
`;

export const parseConfluenceEditorSettings = (confluenceEditorSettings?: any): UserPreferences => {
	if (confluenceEditorSettings && (confluenceEditorSettings satisfies UserPreferences)) {
		return confluenceEditorSettings as UserPreferences;
	}

	return {} as UserPreferences;
};

export class ConfluenceEditorPreferencesProvider implements UserPreferencesProvider {
	private userPreferences: UserPreferences = {};
	// We use getApolloClient to get the apollo client instance
	// This is to avoid adding required apollo context
	private apolloClient = getApolloClient();
	private initialized = false;

	public constructor() {
		this.init();
	}

	public init(): void {
		if (!this.initialized) {
			this.fetchPreferences().catch(() => {
				// Do nothing
			});
		}
	}

	// add this to implment the UserPreferensProvider interface.
	public loadPreferences(): Promise<UserPreferences> {
		return Promise.reject();
	}

	private async fetchPreferences(): Promise<void> {
		if (!this.apolloClient) {
			return Promise.reject('No apollo client');
		}

		try {
			if (!this.initialized) {
				const { data } = await queryInterop<
					EditorPreferencesQuery$data,
					EditorPreferencesQuery$variables
				>({
					query: EditorPreferencesQuery,
				});
				this.userPreferences = parseConfluenceEditorSettings(
					data?.userPreferences?.confluenceEditorSettings,
				);
				this.initialized = true;
			}
			return Promise.resolve();
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async updatePreference<K extends keyof UserPreferences>(
		key: K,
		value: UserPreferences[K],
	): Promise<void> {
		if (!this.apolloClient) {
			return Promise.reject('No apollo client');
		}

		this.userPreferences[key] = value;

		const variables = {
			[key as keyof EditorPreferencesMutation$variables]: value,
		} as EditorPreferencesMutation$variables;

		try {
			const response = await mutateInterop<
				EditorPreferencesMutation$data,
				EditorPreferencesMutation$variables
			>({
				mutation: EditorPreferencesMutation,
				variables,
				update: () => {
					// this is required to indicate toolbar changes to progressive object header
					writeQueryInterop({
						query: EditorPreferencesQuery,
						data: {
							userPreferences: {
								__typename: 'UserPreferences',
								confluenceEditorSettings: {
									__typename: 'ConfluenceEditorSettings',
									toolbarDockingInitialPosition: value === 'top' ? value : null, // Null is required since apollo cache uses shallow reference equality
								},
							},
						},
					});
				},
			});

			const confluenceEditorSettings = parseConfluenceEditorSettings(
				response.data?.updateUserPreferences?.confluenceEditorSettings,
			);

			this.userPreferences[key] = confluenceEditorSettings[key];

			return Promise.resolve();
		} catch (error) {
			return Promise.reject(error);
		}
	}

	getPreference<K extends keyof UserPreferences>(key: K): UserPreferences[K] | undefined | null {
		return this.userPreferences[key];
	}
}

export const useEditorPreferencesProvider = (options?: {
	getCurrentIsLivePage?: () => boolean;
}): {
	editorPreferencesProvider: UserPreferencesProviderNext | undefined;
} => {
	const { isLoggedIn } = useSessionData();

	const editorPreferencesProvider = useRef<UserPreferencesProviderNext | undefined>();
	const persistenceAPI = useRef<UserPreferencesPersistenceAPI | undefined>();

	const readFragmentInterop = useReadFragmentInterop({
		fragmentSpec: graphql`
			fragment EditorPreferencesQueryFragment on UserPreferences {
				confluenceEditorSettings {
					toolbarDockingInitialPosition
				}
			}
		`,
	});

	if (!editorPreferencesProvider.current) {
		const isLivePage = options?.getCurrentIsLivePage?.() ?? false;
		persistenceAPI.current = new UserPreferencesPersistenceAPI(isLoggedIn, readFragmentInterop);
		editorPreferencesProvider.current = getUserPreferencesProvider({
			isLivePage,
			persistenceAPI: persistenceAPI.current,
			readFragmentInterop,
		});
	}

	useEffect(() => {
		if (persistenceAPI.current) {
			persistenceAPI.current.setIsLoggedIn(isLoggedIn);
		}
	}, [isLoggedIn]);

	return {
		editorPreferencesProvider: editorPreferencesProvider.current ?? undefined,
	};
};
